import { getCurrentScope, onScopeDispose } from 'vue';
import { initializeApp, getApps } from 'firebase/app';
import { getAuth, signInWithCustomToken } from 'firebase/auth';
import { getDatabase, ref, onValue, get, onChildAdded, query, limitToLast, set } from 'firebase/database';

type InitArgs = [
  config: object,
  customToken: string,
  providerPath: string,
  clientId: string,
  editionId: string,
  userId: string,
  itemState: string
];

type RefreshCreds = () => Promise<{ config: object; token: string }>;

type FirebaseState = {
  loading: boolean;
  connecting: boolean;
  connectionFailure: boolean;
  connectionStatus: string;
  status: string;
  response: unknown;
  activeItemId: string | null;
  activeGroupId: string | null;
};

const initialState = (): FirebaseState => ({
  loading: true,
  connecting: true,
  connectionFailure: false,
  connectionStatus: 'Initializing',
  status: 'Loading',
  response: null,
  activeItemId: null,
  activeGroupId: null
});

/*
 * Client-only Firebase plumbing. The only writers are `initFirebase`
 * and `cleanup`, both of which run only on the client (`initFirebase`
 * is invoked from `useCollection`'s `onMounted`; `cleanup` from
 * `onScopeDispose` or `retry`). On the server these stay at their
 * initial null/empty values, so module scope here cannot leak across
 * SSR requests. On the client, the same Nuxt app instance lives across
 * SPA navigations within a tab, and module scope correctly survives
 * navigations.
 */
let firebaseApp: ReturnType<typeof initializeApp> | null = null;
let database: ReturnType<typeof getDatabase> | null = null;
let auth: ReturnType<typeof getAuth> | null = null;
let unsubscribers: Array<() => void> = [];
let watchdogTimer: ReturnType<typeof setTimeout> | null = null;
let lastInitArgs: InitArgs | null = null;
/*
 * Generation counter — `initFirebase` and `attachListeners` capture
 * this at start; `cleanup()` and the start of each new `initFirebase`
 * bump it. Post-await checks bail when the captured value no longer
 * matches the current `initGen`. Replaces the previous `disposed`
 * boolean — initGen-based checks are immune to a new init resetting
 * `disposed = false` on top of in-flight callbacks from a torn-down
 * session.
 */
let initGen = 0;

const clearWatchdog = () => {
  if (watchdogTimer) {
    clearTimeout(watchdogTimer);
    watchdogTimer = null;
  }
};

const unsubscribeAll = () => {
  unsubscribers.forEach((unsub) => {
    try { unsub(); } catch (e) { console.warn('[useFirebase] unsubscribe failed', e); }
  });
  unsubscribers = [];
};

const cleanup = () => {
  initGen++;
  unsubscribeAll();
  clearWatchdog();
};

const scrollInToView = (id: string) => {
  /*
   * TODO(FL-12055 follow-up): `id` originates from Firebase RTDB; wrap
   * with CSS.escape(id) to harden against selector syntax errors when
   * ids contain CSS metacharacters.
   */
  const element = document.querySelector(`[data-id="${id}"]`);

  if (element) {
    element.scrollIntoView({ block: 'center' });
    element.classList.add('highlighted');
    setTimeout(() => {
      element.classList.remove('highlighted');
    }, 3000);
  } else {
    console.log(`Element with id: ${id} not found`);
  }
};

export default function () {
  /*
   * Reactive state via `useState`: request-scoped on the server (no
   * cross-request leak), tab-scoped on the client (persists across SPA
   * navigations like the previous module-level singleton). Both
   * consumers (preview.vue and useCollection) call useFirebase() within
   * the same request and get the same ref via useState's keyed lookup,
   * preserving cross-consumer sharing.
   */
  const state = useState<FirebaseState>('useFirebase:state', () => initialState());
  const isSaving = useState<boolean>('useFirebase:isSaving', () => false);

  /*
   * The closures below (`actions`, `attachListeners`, `startWatchdog`,
   * `initFirebase`, `retry`) live inside the factory so they close over
   * the request-scoped `state` / `isSaving` refs. They are recreated
   * per consumer call, but all close over the same shared refs, so
   * cross-consumer sharing of writes is preserved. Listener callbacks
   * registered inside `attachListeners` outlive the factory call;
   * `cleanup()` unsubscribes them, after which their closures are GC'd.
   */

  const actions = {
    missingConfig: () => {
      state.value.connectionFailure = true;
      state.value.connecting = false;
      state.value.loading = false;
      state.value.connectionStatus = 'Realtime config is missing';
    },

    connecting: () => {
      state.value.connectionStatus = 'Connecting to realtime database …';
      state.value.connecting = true;
    },

    connectionFailure: (err: unknown) => {
      state.value.connectionFailure = true;
      state.value.connecting = false;
      state.value.loading = false;
      const message = err instanceof Error ? err.message : String(err);
      state.value.connectionStatus = `Error connecting to realtime database: ${message}`;
    },

    connected: () => {
      state.value.connecting = false;
      state.value.connectionFailure = false;
      state.value.connectionStatus = 'Connected to realtime database';
    },

    noActiveDraft: () => {
      state.value.loading = false;
      state.value.connectionFailure = false;
      state.value.status = 'No realtime edition data available. Do you have the edition open in DrEdition?';
      clearWatchdog();
    },

    setActiveItem: (itemId: string | null) => {
      state.value.activeItemId = itemId;
    },

    setActiveGroup: (groupId: string | null) => {
      state.value.activeGroupId = groupId;
    },

    setDraft: (draft: unknown) => {
      state.value.response = draft;
      state.value.status = '';
      state.value.loading = false;
      state.value.connectionFailure = false;
    },

    watchdogTimeout: () => {
      state.value.connectionFailure = true;
      state.value.connecting = false;
      state.value.loading = false;
      state.value.connectionStatus = 'Preview data did not arrive. Click retry to reconnect.';
    },

    refreshing: () => {
      /*
       * Keeps `connectionFailure` = true so the existing banner updates
       * in place to "Refreshing credentials …" instead of disappearing
       * during the network round-trip.
       */
      state.value.connecting = true;
      state.value.connectionStatus = 'Refreshing credentials …';
    }
  };

  const attachListeners = (db: ReturnType<typeof getDatabase>, providerPath: string, clientId: string, editionId: string, userId: string, itemState: string) => {
    /*
     * Capture the current generation; every callback below bails via
     * `isStale()` if a later cleanup() or initFirebase() has bumped
     * initGen, even if `disposed` semantics would have been reset by a
     * remount.
     */
    const myGen = initGen;
    const isStale = () => myGen !== initGen;

    const { replaceUrl } = useUrlReplace();

    const draftRef = ref(db, replaceUrl(providerPath, { clientId, editionId, userId, refType: 'regularDrafts' }));
    const activeVariantRef = ref(db, replaceUrl(providerPath, { clientId, editionId, userId, refType: 'activeVariantName' }));
    const uiEventsRef = ref(db, replaceUrl(providerPath, { clientId, editionId, userId, refType: 'uiEvents' }));
    const activeItemRef = ref(db, replaceUrl(providerPath, { clientId, editionId, userId, refType: 'activeItem' }));

    const onListenerError = (label: string) => (err: Error) => {
      if (isStale()) { return; }
      console.error(`[useFirebase] ${label} listener error`, err);
      actions.connectionFailure(err);
    };

    // Surface online/offline transitions for monitoring.
    const connectedRef = ref(db, '.info/connected');
    unsubscribers.push(onValue(connectedRef, (snap) => {
      if (isStale()) { return; }
      /*
       * TODO(FL-12055 follow-up): `useRuntimeConfig()` is officially
       * supported only inside setup; capture `publication` once in
       * `attachListeners`/`initFirebase` scope and close over it
       * instead of calling here.
       */
      const publication = useRuntimeConfig().public?.name;
      console.info('[useFirebase] .info/connected', {
        publication,
        editionId,
        userId,
        connected: snap.val() === true
      });
    }, (err) => {
      console.error('[useFirebase] .info/connected listener error', err);
    }));

    unsubscribers.push(onValue(draftRef, async (snapshot) => {
      if (isStale()) { return; }
      if (!(snapshot?.val()?.positions)) {
        actions.noActiveDraft();
        return;
      }
      const activeVariantSnapshot = await get(activeVariantRef);
      if (isStale()) { return; }
      const activeItemSnapshot = await get(activeItemRef);
      if (isStale()) { return; }
      if (activeItemSnapshot.val()) {
        setTimeout(() => scrollInToView(activeItemSnapshot.val()), 500);
      }
      actions.setDraft(usePreviewTranslator().translate(snapshot.val()?.positions, activeVariantSnapshot?.val() || '', itemState));
      isSaving.value = false;
      clearWatchdog();
    }, onListenerError('draft')));

    unsubscribers.push(onValue(activeVariantRef, async (snapshot) => {
      if (isStale()) { return; }
      const originDataSnapShot = await get(draftRef);
      if (isStale()) { return; }
      if (snapshot?.val() && originDataSnapShot?.val()) {
        actions.setDraft(usePreviewTranslator().translate(originDataSnapShot.val()?.positions, snapshot.val() || '', itemState));
        const activeItemSnapshot = await get(activeItemRef);
        if (isStale()) { return; }
        if (activeItemSnapshot.val()) {
          setTimeout(() => scrollInToView(activeItemSnapshot.val()), 500);
        }
      }
    }, onListenerError('activeVariant')));

    unsubscribers.push(onValue(activeItemRef, () => {
      /*
       * This event is triggered only once when an item inside the
       * drEdition is clicked. Also when an item id is passed in
       * drEdition url.
       */
    }, onListenerError('activeItem')));

    const uiEventsQuery = query(uiEventsRef, limitToLast(1));
    unsubscribers.push(onChildAdded(uiEventsQuery, (snapshot) => {
      if (isStale()) { return; }
      /*
       * TODO(FL-12055 follow-up): `snapshot.val()` is whatever
       * drEdition writes; if it is ever non-string the `.split(':')`
       * below throws a TypeError. The throw does NOT unsubscribe the
       * listener (RTDB doesn't tear down on callback throw) but it
       * surfaces as an uncaught error. Harden with
       * `typeof value === 'string'` and a length check before
       * destructuring.
       */
      const [event, type, id] = snapshot.val().split(':');
      if (event !== 'click') { return; }
      /*
       * This event is likely to often be triggered at the same time as
       * activeItemRef, but will also trigger on clicks when the item is
       * already active, and will not trigger if active item is selected
       * through some other means than clicking the item in the edition
       * list. It's recommended to listen to both events, but to dedupe
       * any heavy processing the event might trigger in your system.
       */
      if (type === 'item') {
        actions.setActiveItem(id);
        scrollInToView(id);
        return;
      }
      if (type === 'group') {
        actions.setActiveGroup(id);
      }
    }, onListenerError('uiEvents')));

    const stopClick = useEventListener('click', (event) => {
      if (isStale()) { return; }
      event.preventDefault();
      event.stopImmediatePropagation();

      if (!(event.target instanceof HTMLElement)) { return; }

      const element = event.target?.closest('[data-id]');
      if (!element) { return; }

      const placeholder = element.closest('.dn-placeholder');
      const dataId = placeholder ? placeholder.getAttribute('data-id') : element.getAttribute('data-id');

      if (activeItemRef) {
        set(activeItemRef, dataId)
          .then(() => { console.log('Active Item successfully updated:', dataId); })
          .catch((err) => { console.error('Error setting active item ID:', dataId, err); });
      }
    });
    unsubscribers.push(stopClick);
  };

  const startWatchdog = () => {
    clearWatchdog();
    /*
     * TODO(FL-12055 follow-up): `useRuntimeConfig()` is also called
     * inside the setTimeout below — capture `firebasePublic` and
     * `publication` once at attach time and close over them instead of
     * re-resolving outside setup context.
     */
    const firebasePublic = (useRuntimeConfig().public as { firebase?: { watchdogMs?: number } })?.firebase;
    const watchdogMs = firebasePublic?.watchdogMs ?? 8000;
    watchdogTimer = setTimeout(() => {
      watchdogTimer = null;
      if (!state.value.loading || state.value.response !== null) { return; }

      const publication = useRuntimeConfig().public?.name;
      console.warn('[useFirebase] watchdog: first snapshot did not arrive', {
        publication,
        editionId: lastInitArgs?.[4],
        userId: lastInitArgs?.[5]
      });

      actions.watchdogTimeout();
    }, watchdogMs);
  };

  const initFirebase = async (config: object, customToken: string, providerPath: string, clientId: string, editionId: string, userId: string, itemState: string) => {
    /*
     * Capture this init's generation. If `cleanup()` or another
     * `initFirebase()` bumps `initGen` while we're awaiting signIn,
     * we bail out below.
     */
    const myGen = ++initGen;
    lastInitArgs = [config, customToken, providerPath, clientId, editionId, userId, itemState];
    // Reset state so re-mount after a previous cleanup starts fresh.
    state.value = initialState();
    try {
      if (!(config && customToken)) {
        actions.missingConfig();
        return;
      }
      actions.connecting();

      /*
       * Assert config matches whatever app we're about to (re)use, on
       * every initFirebase, not only the first. Without this, a second
       * initFirebase call with a different config silently reuses the
       * cached `firebaseApp` and routes traffic to the wrong RTDB.
       */
      const incoming = config as { databaseURL?: string; apiKey?: string };
      const checkMatch = (existing: { databaseURL?: string; apiKey?: string }) => {
        if (existing.databaseURL !== incoming.databaseURL || existing.apiKey !== incoming.apiKey) {
          throw new Error('Firebase config mismatch: existing app has a different databaseURL or apiKey.');
        }
      };

      /*
       * Reuse existing default app on SPA revisits to avoid
       * `Firebase App named '[DEFAULT]' already exists`.
       */
      if (!firebaseApp) {
        const existing = getApps();
        if (existing.length > 0) {
          checkMatch(existing[0].options);
          firebaseApp = existing[0];
        } else {
          firebaseApp = initializeApp(config);
        }
        database = getDatabase(firebaseApp);
        auth = getAuth(firebaseApp);
      } else {
        checkMatch(firebaseApp.options);
      }

      if (!database) { throw new Error('Database is not initialized'); }
      if (!auth) { throw new Error('Auth is not initialized'); }

      /*
       * Race signIn against the watchdog timeout so a hung network
       * surfaces as failure instead of leaving the UI stuck on
       * "Connecting…" forever.
       */
      const signInWatchdogMs = (useRuntimeConfig().public as { firebase?: { watchdogMs?: number } })?.firebase?.watchdogMs ?? 8000;
      const signInPromise = signInWithCustomToken(auth, customToken);
      signInPromise.catch(() => { /* swallow if timeout already won the race */ });
      let timeoutId: ReturnType<typeof setTimeout> | null = null;
      const timeoutPromise = new Promise<never>((_resolve, reject) => {
        timeoutId = setTimeout(
          () => reject(new Error(`signInWithCustomToken did not resolve within ${signInWatchdogMs}ms`)),
          signInWatchdogMs
        );
      });
      timeoutPromise.catch(() => { /* swallow if signIn already won the race */ });

      try {
        await Promise.race([signInPromise, timeoutPromise]);
      } catch (e) {
        if (myGen !== initGen) { return; }
        console.error('Authentication Failed', e);
        actions.connectionFailure(e);
        return;
      } finally {
        if (timeoutId) { clearTimeout(timeoutId); }
      }
      if (myGen !== initGen) { return; }
      actions.connected();

      attachListeners(database, providerPath, clientId, editionId, userId, itemState);
      startWatchdog();
    } catch (err) {
      if (myGen !== initGen) { return; }
      actions.connectionFailure(err);
      console.error('Firebase initialization failed:', err);
      /*
       * TODO(FL-12055 follow-up): this `throw` becomes an unhandled
       * rejection because `initFirebase` is awaited inside
       * `onMounted(async () => …)` in `useCollection.ts`. The
       * `actions.connectionFailure(err)` above is the actual recovery —
       * either drop this throw or surface it through a Nuxt-aware error
       * path.
       */
      throw createError({
        statusCode: 401,
        message: 'Unauthorized to initialize firebase'
      });
    }
  };

  const retry = async (refresh?: RefreshCreds) => {
    if (!lastInitArgs) { return; }
    if (refresh) {
      /*
       * Show 'Refreshing credentials …' while we await; cleanup() does
       * not reset state, so the message persists.
       */
      actions.refreshing();
      cleanup();
      /*
       * Capture the generation AFTER cleanup's bump so we can detect a
       * further unmount-during-refresh (which would leak listeners onto
       * the singleton with no consumer).
       */
      const myGen = initGen;
      try {
        const fresh = await refresh();
        if (myGen !== initGen) { return; }
        const nextArgs: InitArgs = [
          fresh.config,
          fresh.token,
          lastInitArgs[2],
          lastInitArgs[3],
          lastInitArgs[4],
          lastInitArgs[5],
          lastInitArgs[6]
        ];
        await initFirebase(...nextArgs);
      } catch (e) {
        if (myGen !== initGen) { return; }
        console.error('[useFirebase] retry: failed to refresh credentials', e);
        actions.connectionFailure(e);
      }
    } else {
      console.warn('[useFirebase] retry: replaying cached token — it may have expired. Consider passing a RefreshCreds callback for fresh credentials.');
      cleanup();
      await initFirebase(...lastInitArgs);
    }
  };

  /*
   * Client-only: cleanup() mutates module-scope singleton state
   * (initGen++) and must not run during SSR teardown.
   */
  if (import.meta.client && getCurrentScope()) {
    onScopeDispose(() => cleanup());
  }
  /*
   * When this composable is freshly consumed and there is no live work,
   * reset state so a stale `connectionFailure: true` from a previous
   * SPA navigation doesn't bleed into this mount and flash the banner
   * before initFirebase runs. No-op on the server because useState
   * already seeded `state` with initialState() for this request.
   */
  if (unsubscribers.length === 0 && !watchdogTimer) {
    state.value = initialState();
  }

  return {
    initFirebase,
    state,
    isSaving,
    cleanup,
    retry
  };
}
